import type { Client, ExecutionResult, Sink, SubscribePayload } from "graphql-ws";

import { AuthProvider } from "@/enums/auth-provider";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import type { WebSocketClient } from "@/lib/interfaces/websocket-client";

/**
 * Dispatches WebSocket calls to the correct provider implementation.
 *
 * The active implementation is resolved per-call based on the current auth provider —
 * mirroring the same strategy used by `AuthenticationClient`.
 */
export class WebsocketClientDispatch implements WebSocketClient {
  constructor(
    private readonly auth0WebsocketClient: Client,
    private readonly stytchWebsocketClient: Client,
    private readonly authenticationClient: AuthenticationClient,
  ) {}

  private get activeClient(): Client {
    return this.authenticationClient.activeProvider === AuthProvider.Stytch
      ? this.stytchWebsocketClient
      : this.auth0WebsocketClient;
  }

  iterate<TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    payload: SubscribePayload,
  ): AsyncIterableIterator<ExecutionResult<TData, TVariables>> {
    return this.activeClient.iterate<TData, TVariables>(payload);
  }

  subscribe<TData = Record<string, unknown>, TVariables = Record<string, unknown>>(
    payload: SubscribePayload,
    sink: Sink<ExecutionResult<TData, TVariables>>,
  ): () => void {
    return this.activeClient.subscribe<TData, TVariables>(payload, sink);
  }

  dispose(): void | Promise<void> {
    return this.activeClient.dispose();
  }

  terminate(): void {
    this.activeClient.terminate();
  }
}
